import React, { useContext, useState } from 'react';

import { FilePlus2, Merge, Trash } from 'lucide-react';

import type { Board } from 'common/api/endpoints/boards';
import type { Company } from 'common/api/endpoints/companies';
import type { Viewer } from 'common/api/endpoints/viewer';
import {
  AutopilotActionSourceType,
  AutopilotActionType,
  isDraftActionItem,
  isIdeaActionItem,
  isInsightActionItem,
  isPostActionItem,
} from 'common/constants/autopilotActions';
import { BoardsContext } from 'common/containers/BoardsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import MergePostDropdown from 'common/post/MergePostDropdown';
import AlertDialog from 'common/uiV3/AlertDialog';
import Button from 'common/uiV3/Button';
import Flex from 'common/uiV3/Flex';
import Popover from 'common/uiV3/Popover';
import Tooltip from 'common/uiV3/Tooltip';
import VStack from 'common/uiV3/VStack';
import hasPermission from 'common/util/hasPermission';
import type { ActionRow as Row } from '../types';

type Props = {
  row: Row;
  boards: Board[];
};

enum Action {
  create = 'create',
  merge = 'merge',
  delete = 'delete',
}

const ActionLabelMap = {
  [AutopilotActionType.merge]: 'Merged',
  [AutopilotActionType.create]: 'Created',
  [AutopilotActionType.dismiss]: 'Dismissed',
  default: 'Acted',
} as const;

const MissingPermissionMessage = 'You do not have permission to perform this action.';

const getActionDate = (item: Row['item']) => {
  return new Date(item.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const fetchPermissions = (viewer: Viewer, company: Company) => {
  return {
    voteOnBehalf: hasPermission('voteOnBehalf', company, viewer),
    mergePosts: hasPermission('mergePosts', company, viewer),
    deletePosts: hasPermission('deletePosts', company, viewer),
    deleteComments: hasPermission('deleteComments', company, viewer),
  };
};

const getLabels = (row: Row) => {
  if (isInsightActionItem(row.item)) {
    return {
      // createTooltip: 'Create as new idea instead',
      deleteTooltip: 'Delete insight',
      // mergeTooltip: 'Move into a different idea',
      deleteModalCTA: 'Delete insight',
      deleteModalHeader: 'Are you sure you want to delete this insight?',
      deleteModalBody:
        'The insight will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
    };
  } else if (isIdeaActionItem(row.item)) {
    if (row.item.actionType === AutopilotActionType.merge) {
      return {
        // createTooltip: 'Create as new idea instead',
        deleteTooltip: 'Undo merge',
        deleteModalCTA: 'Undo merge',
        deleteModalHeader: 'Are you sure you want to undo this merge?',
        deleteModalBody:
          'The original idea will be restored and visible to you and other users in your workspace.',
        // mergeTooltip: 'Merge into a different idea',
      };
    } else if (row.item.actionType === AutopilotActionType.create) {
      return {
        // createTooltip: 'Create as new post instead',
        deleteTooltip: 'Delete idea',
        // mergeTooltip: 'Move into a different idea',
        deleteModalCTA: 'Delete idea',
        deleteModalHeader: 'Are you sure you want to delete this idea?',
        deleteModalBody:
          'The idea will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
      };
    } else if (row.item.actionType === AutopilotActionType.dismiss) {
      return {
        deleteTooltip: 'Restore idea',
        deleteModalCTA: 'Restore idea',
        deleteModalHeader: 'Are you sure you want to restore this idea?',
        deleteModalBody:
          'The idea will be restored and visible to you and other users in your workspace.',
      };
    }
  }

  if (row.item.createdPostID) {
    return {
      deleteTooltip: 'Delete post',
      mergeTooltip: 'Merge into existing post instead',
      deleteModalCTA: 'Delete post',
      deleteModalHeader: 'Are you sure you want to delete this post?',
      deleteModalBody:
        'The post will no longer be visible to you or other users in your workspace. This action cannot be reversed.',
    };
  } else if (row.item.actionType === AutopilotActionType.dismiss) {
    return {
      createTooltip: 'Create as new post instead',
      mergeTooltip: 'Merge into existing post instead',
    };
  }

  if (row.item.source === 'canny') {
    return {
      createTooltip: 'Create as new post instead',
      deleteTooltip: 'Undo merge and delete duplicate',
      mergeTooltip: 'Merge into a different post',
      deleteModalCTA: 'Undo merge and delete',
      deleteModalHeader: 'Are you sure you want to undo this merge and delete the duplicate post?',
      deleteModalBody:
        'The posts will be unmerged, the duplicate post will be deleted and will no longer be visible. This action cannot be reversed.',
    };
  }

  return {
    createTooltip: 'Create as new post instead',
    deleteTooltip: 'Undo merge and dismiss with no action',
    mergeTooltip: 'Merge into a different post',
    deleteModalCTA: 'Undo merge and dismiss',
    deleteModalHeader: 'Are you sure you want to undo this merge and dismiss the feedback?',
    deleteModalBody:
      'The posts will be unmerged and the original feedback post will be dismissed. This action cannot be reversed.',
  };
};

const AuditLogActionsCell = ({ row }: Props) => {
  const [loadingAction, setLoadingAction] = useState<Action | null>();
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const viewer = useContext<Viewer>(ViewerContext);
  const company = useContext<Company>(CompanyContext);
  const boards = useContext(BoardsContext);

  const userPermissions = fetchPermissions(viewer, company);

  const canPerformMerge =
    row.item.sourceType === AutopilotActionSourceType.post
      ? userPermissions.mergePosts
      : userPermissions.voteOnBehalf && userPermissions.deleteComments;

  // to perform any action the user must be able to undo the initial action
  let canUndoAction = false;
  if (row.item.createdPostID) {
    canUndoAction = userPermissions.deletePosts;
  } else if (row.item.actionType === 'dismiss') {
    canUndoAction = true;
  } else if (canPerformMerge) {
    canUndoAction = true;
  }

  const { item, actions } = row;

  const performCreate = async () => {
    setLoadingAction(Action.create);
    await actions.create();
    setLoadingAction(null);
  };

  const performDelete = async () => {
    setLoadingAction(Action.delete);
    await actions.delete();
    setLoadingAction(null);
  };

  const actionsDisabled = !!loadingAction || !canUndoAction;

  // post is used to a) stop the merge dialog recommending the current post via an _id comparison b) find similar suggestions based off title and details
  let post = undefined;
  if (isPostActionItem(item) || isDraftActionItem(item)) {
    if (item.duplicatePost) {
      post = item.duplicatePost;
    } else {
      post = { ...item.sourcePostDraft, _id: item.createdPostID };
    }
  }

  const { mergeTooltip, createTooltip, deleteTooltip, ...modalLabels } = getLabels(row);

  return (
    <VStack className="gap-2">
      <span className="text-secondary-foreground text-base">
        {ActionLabelMap[item.actionType] ?? ActionLabelMap.default} on {getActionDate(item)}
      </span>
      <Flex className="flex-col md:flex-row justify-between items-center gap-2">
        {mergeTooltip ? (
          <Popover open={mergeOpen} onOpenChange={setMergeOpen}>
            <div>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <div>
                    <Popover.Anchor asChild>
                      <Button
                        aria-label={mergeTooltip}
                        disabled={actionsDisabled || !canPerformMerge}
                        isLoading={loadingAction === Action.merge}
                        onClick={() => setMergeOpen(true)}
                        iconOnly
                        variant="outlined">
                        <Merge />
                      </Button>
                    </Popover.Anchor>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Content side="left">
                  {canUndoAction && canPerformMerge ? mergeTooltip : MissingPermissionMessage}
                </Tooltip.Content>
              </Tooltip>
            </div>
            <Popover.Content align="end" className="p-0 min-w-[300px]">
              <MergePostDropdown
                boards={boards}
                placeholder="Search all posts..."
                onPostSelected={actions.merge}
                post={post}
              />
            </Popover.Content>
          </Popover>
        ) : null}
        {!item.createdPostID && createTooltip && (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <div>
                <Button
                  aria-label={createTooltip}
                  disabled={actionsDisabled}
                  isLoading={loadingAction === Action.create}
                  onClick={performCreate}
                  iconOnly
                  variant="outlined">
                  <FilePlus2 />
                </Button>
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content side="left">
              {canUndoAction ? createTooltip : MissingPermissionMessage}
            </Tooltip.Content>
          </Tooltip>
        )}
        {deleteTooltip && (
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <div>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <div>
                    <Button
                      aria-label={deleteTooltip}
                      disabled={actionsDisabled}
                      isLoading={loadingAction === Action.delete}
                      onClick={() => setDeleteOpen(true)}
                      iconOnly
                      variant="outlined">
                      <Trash />
                    </Button>
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Content side="left">
                  {canUndoAction ? deleteTooltip : MissingPermissionMessage}
                </Tooltip.Content>
              </Tooltip>
            </div>
            <AlertDialog.Content>
              <AlertDialog.Header>
                <AlertDialog.Title>{modalLabels.deleteModalHeader}</AlertDialog.Title>
                <AlertDialog.Description>{modalLabels.deleteModalBody}</AlertDialog.Description>
              </AlertDialog.Header>
              <AlertDialog.Footer>
                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                <AlertDialog.Action asChild color="error">
                  <Button
                    type="button"
                    isLoading={loadingAction === Action.delete}
                    onClick={performDelete}>
                    {modalLabels.deleteModalCTA}
                  </Button>
                </AlertDialog.Action>
              </AlertDialog.Footer>
            </AlertDialog.Content>
          </AlertDialog>
        )}
      </Flex>
    </VStack>
  );
};

export default AuditLogActionsCell;
